import React, { useState, useEffect } from 'react';
import { API_BASE, API_PREFIX, authFetch } from './apiClient';

// Helper to get previous, current, and next month in YYYY-MM format
function getThreeMonths() {
  const months = [];
  const now = new Date();

  // Previous month
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  // Current month
  const curr = new Date(now.getFullYear(), now.getMonth(), 1);
  // Next month
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  [prev, curr, next].forEach(d => {
    const month = d.getMonth() + 1;
    months.push(`${d.getFullYear()}-${month < 10 ? '0' : ''}${month}`);
  });

  return months;
}

const AdminAddBill = () => {
  const recentMonths = getThreeMonths();
  const [formData, setFormData] = useState({
    tenantName: '',
    monthYear: recentMonths[1], // Default to current month
    billType: 'RENT',
    rent: '',
    water: '',
    electricity: '',
  miscellaneous: '' // optional maintenance / balance / other charges
  });
  const isElectricity = formData.billType === 'ELECTRICITY';

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    authFetch(`${API_BASE}${API_PREFIX}/users/names`)
      .then(res => res.json())
      .then(data => setTenants(data))
      .catch(err => console.error('Failed to fetch tenants', err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'billType') {
      // Clear fields that don't apply to the new type, so a leftover value
      // from Rent doesn't get silently submitted with an Electricity bill.
      setFormData(prev => (
        value === 'ELECTRICITY'
          ? { ...prev, billType: value, rent: '', water: '', miscellaneous: '' }
          : { ...prev, billType: value, electricity: '' }
      ));
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
  const res = await authFetch(`${API_BASE}${API_PREFIX}/tenants/addBill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        // Try to read error message from backend
        let msg = 'Failed to add bill';
        try {
          const data = await res.json();
          if (data && data.error) msg = data.error;
        } catch {}
        throw new Error(msg);
      }

      setFormData({
        tenantName: '',
        monthYear: recentMonths[1],
        billType: 'RENT',
        rent: '',
        water: '',
  electricity: '',
  miscellaneous: ''
      });
      alert(`${isElectricity ? 'Electricity' : 'Rent'} bill added and email sent!`);
    } catch (err) {
      setError(err.message || 'Error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: '400px',
        margin: '0 auto',
        padding: '32px',
        background: 'var(--surface)',
        borderRadius: '16px',
        boxShadow: 'var(--shadow)',
      }}
    >
      <h2 style={{ color: 'var(--primary)', textAlign: 'center', marginBottom: '24px', letterSpacing: '1px' }}>
        Add Bill
      </h2>
      <form onSubmit={handleSubmit}>
        {/* Show loading message */}
        {loading && (
          <div style={{ color: 'var(--primary)', marginBottom: '12px', textAlign: 'center' }}>
            Adding bill...
          </div>
        )}
        {/* Show error message */}
        {error && (
          <div style={{ color: 'red', marginBottom: '12px', textAlign: 'center' }}>
            {error}
          </div>
        )}
        <select
          name="tenantName"
          value={formData.tenantName}
          onChange={handleChange}
          required
          style={{
            color: formData.tenantName ? 'var(--ink)' : 'var(--muted)',
            backgroundColor: '#fff',
            border: '1px solid var(--line-strong)',
            borderRadius: '6px',
            padding: '10px',
            width: '100%',
            marginBottom: '16px',
            fontSize: '16px',
          }}
        >
          <option value="">Select Tenant</option>
          {tenants.map((tenant, idx) => {
            const name =
              typeof tenant === 'string'
                ? tenant
                : tenant.tenantName || tenant.name || tenant.username || '';
            return (
              <option key={idx} value={name}>
                {name || `Tenant ${idx + 1}`}
              </option>
            );
          })}
        </select>
        <select
          name="monthYear"
          value={formData.monthYear}
          onChange={handleChange}
          required
          style={{
            width: '100%',
            padding: '10px',
            marginBottom: '12px',
            border: '1px solid var(--line-strong)',
            borderRadius: '6px',
            fontSize: '16px',
            background: '#fff',
            color: 'var(--ink)',
          }}
        >
          {recentMonths.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        {/* Electricity bills consistently arrive after rent is due, so they're
            created as a separate bill from rent/water/misc -- not bundled
            into one bill that would have to wait on the electricity figure. */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          <label style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            padding: '10px', border: `1px solid ${formData.billType === 'RENT' ? 'var(--primary)' : 'var(--line-strong)'}`,
            borderRadius: '6px', cursor: 'pointer', background: formData.billType === 'RENT' ? 'var(--primary-tint)' : '#fff',
            fontSize: '15px', color: 'var(--ink)',
          }}>
            <input type="radio" name="billType" value="RENT" checked={formData.billType === 'RENT'} onChange={handleChange} />
            Rent
          </label>
          <label style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            padding: '10px', border: `1px solid ${isElectricity ? 'var(--primary)' : 'var(--line-strong)'}`,
            borderRadius: '6px', cursor: 'pointer', background: isElectricity ? 'var(--primary-tint)' : '#fff',
            fontSize: '15px', color: 'var(--ink)',
          }}>
            <input type="radio" name="billType" value="ELECTRICITY" checked={isElectricity} onChange={handleChange} />
            Electricity
          </label>
        </div>
        {!isElectricity && (
          <>
            <input
              name="rent"
              placeholder="Rent"
              value={formData.rent}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '12px',
                border: '1px solid var(--line-strong)',
                borderRadius: '6px',
                fontSize: '16px',
              }}
            />
            <input
              name="water"
              placeholder="Water Bill"
              value={formData.water}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '12px',
                border: '1px solid var(--line-strong)',
                borderRadius: '6px',
                fontSize: '16px',
              }}
            />
            <input
              name="miscellaneous"
              placeholder="Miscellaneous / Maintenance (optional)"
              value={formData.miscellaneous}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '20px',
                border: '1px solid var(--line-strong)',
                borderRadius: '6px',
                fontSize: '16px',
              }}
            />
          </>
        )}
        {isElectricity && (
          <input
            name="electricity"
            placeholder="Electricity Bill"
            value={formData.electricity}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '20px',
              border: '1px solid var(--line-strong)',
              borderRadius: '6px',
              fontSize: '16px',
            }}
          />
        )}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '12px',
            background: 'var(--grad-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontWeight: 'bold',
            fontSize: '17px',
            letterSpacing: '1px',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Adding...' : 'Add Bill'}
        </button>
      </form>
    </div>
  );
};

export default AdminAddBill;
